import { EnumApiTokenCostRoute, IApiTokenCostConfigEntry } from '@libs/constants';
import { IUserFlyingUploadSettings } from '@domains/user/shared';
import { EnumAiModel } from '@domains/ai-toolkit/shared';
import { Util } from '@libs/utilities/util';

import { EnumFlyingPodSetupPlatform, IFlyingFlaiAgentRequest, IFlyingGenerateByKeywordsRequest, IFlyingGenerateByTemplateRequest, IFlyingGenerateByVisionRequest, IFlyingMbaSetupRequest } from '../interfaces';


/**------------------------------------------------------
 * Ai Listing API Token Config
 */
export const AI_LISTING_API_TOKEN_CONFIG: IAiListingApiTokenConfig = {
	[EnumApiTokenCostRoute.ListingByVision]: {
		name			: 'Listing By Vision API',
		description		: 'This is the description of Listing By Vision API',
		reasonText		: 'Listing By Vision',
		apiTokenCosts	: {
			model		: {
				[EnumAiModel.Gpt4o]			: 1,
				[EnumAiModel.Gpt4oMini]		: 1,
				[EnumAiModel.Gpt5]			: 0.85,
				[EnumAiModel.Gpt5Mini]		: 0.5,
				[EnumAiModel.Gpt5Nano]		: 0.25,
				[EnumAiModel.Gpt5_1]		: 1,
				[EnumAiModel.Gpt5_6Luna]	: 0.8,
				[EnumAiModel.Gpt6Luna]		: 0.1,
				[EnumAiModel.Gpt6_1Terra]	: 0.3,
				[EnumAiModel.Gpt5_4Mini]	: 0.5
			}
		},
		costCalculateFunction: (options: IFlyingGenerateByVisionRequest & IUserFlyingUploadSettings) => {

			//0 - calculate the total active languages
			let languagesCount: number = 0;
			if (options.setupPlatforms?.length) {
				if (options.setupPlatforms.includes(EnumFlyingPodSetupPlatform.MBA) && options.aiListing.mba.isActive) {
					languagesCount += Math.max(1, options.aiListing.mba.languages.length);
				}
				if (options.setupPlatforms.includes(EnumFlyingPodSetupPlatform.Global) && options.aiListing.global.isActive) {
					languagesCount += Math.max(1, options.aiListing.global.languages.length);
				}
				if (options.setupPlatforms.includes(EnumFlyingPodSetupPlatform.Displate) && options.aiListing.displate.isActive) {
					languagesCount += 1;
				}
				languagesCount += 1; // shared setup recommendation for the selected platforms
			} else {
				if (options.aiListing.mba.isActive) 		languagesCount += options.aiListing.mba.languages.length;
				if (options.aiListing.global.isActive) 		languagesCount += options.aiListing.global.languages.length;
				if (options.aiListing.displate.isActive) 	languagesCount += 1;
			}

			//1 - calculate the costs
			const modelCost			: number = AI_LISTING_API_TOKEN_CONFIG[EnumApiTokenCostRoute.ListingByVision].apiTokenCosts.model[options.model];
			const numberOfEntries	: number = options.entries.length;

			//2 - calculate the total cost
			const totalCost: number = languagesCount * numberOfEntries * modelCost;
			return Util.Number.roundUp(totalCost);
		}
	},
	[EnumApiTokenCostRoute.FlaiAgent]: {
		name			: 'FLAI Agent API',
		description		: 'Generates configured listings and applies POD platform setup',
		reasonText		: 'FLAI Agent',
		apiTokenCosts	: {
			model		: {
				[EnumAiModel.Gpt4o]			: 1,
				[EnumAiModel.Gpt4oMini]		: 1,
				[EnumAiModel.Gpt5]			: 0.85,
				[EnumAiModel.Gpt5Mini]		: 0.5,
				[EnumAiModel.Gpt5Nano]		: 0.25,
				[EnumAiModel.Gpt5_1]		: 1,
				[EnumAiModel.Gpt5_6Luna]	: 0.8,
				[EnumAiModel.Gpt6Luna]		: 0.1,
				[EnumAiModel.Gpt6_1Terra]	: 0.3,
				[EnumAiModel.Gpt5_4Mini]	: 0.5
			}
		},
		costCalculateFunction: (options: IFlyingFlaiAgentRequest & IUserFlyingUploadSettings) => {
			let promptsPerEntry: number = 1; // shared image-based platform recommendation
			const generateListings: boolean = options.generateListings !== false;
			if (generateListings && options.setupPlatforms.includes(EnumFlyingPodSetupPlatform.MBA) && options.aiListing.mba.isActive) {
				promptsPerEntry += Math.max(1, options.aiListing.mba.languages.length);
			}
			if (generateListings && options.setupPlatforms.includes(EnumFlyingPodSetupPlatform.Global) && options.aiListing.global.isActive) {
				promptsPerEntry += Math.max(1, options.aiListing.global.languages.length);
			}
			if (generateListings && options.setupPlatforms.includes(EnumFlyingPodSetupPlatform.Displate) && options.aiListing.displate.isActive) {
				promptsPerEntry += 1;
			}

			const modelCost: number = AI_LISTING_API_TOKEN_CONFIG[EnumApiTokenCostRoute.FlaiAgent].apiTokenCosts.model[options.model];
			return Util.Number.roundUp(promptsPerEntry * options.entries.length * modelCost);
		}
	},
	[EnumApiTokenCostRoute.MbaSetupByVision]: {
		name			: 'MBA Setup By Vision API',
		description		: 'Automatically configures all supported MBA products from a design image',
		reasonText		: 'MBA Setup By Vision',
		apiTokenCosts	: {
			model		: {
				[EnumAiModel.Gpt4o]			: 1,
				[EnumAiModel.Gpt4oMini]		: 1,
				[EnumAiModel.Gpt5]			: 0.85,
				[EnumAiModel.Gpt5Mini]		: 0.5,
				[EnumAiModel.Gpt5Nano]		: 0.25,
				[EnumAiModel.Gpt5_1]		: 1,
				[EnumAiModel.Gpt5_6Luna]	: 0.8,
				[EnumAiModel.Gpt6Luna]		: 0.1,
				[EnumAiModel.Gpt6_1Terra]	: 0.3,
				[EnumAiModel.Gpt5_4Mini]	: 0.5
			}
		},
		costCalculateFunction: (options: IFlyingMbaSetupRequest) => {
			const modelCost: number = AI_LISTING_API_TOKEN_CONFIG[EnumApiTokenCostRoute.MbaSetupByVision].apiTokenCosts.model[options.model];
			return Util.Number.roundUp(modelCost);
		}
	},
	[EnumApiTokenCostRoute.ListingByKeywords]: {
		name			: 'Listing By Keywords API',
		description		: 'This is the description of Listing By Keywords API',
		reasonText		: 'Listing By Keywords',
		apiTokenCosts	: {
			model		: {
				[EnumAiModel.Gpt4o]			: 1,
				[EnumAiModel.Gpt4oMini]		: 1,
				[EnumAiModel.Gpt5]			: 0.85,
				[EnumAiModel.Gpt5Mini]		: 0.5,
				[EnumAiModel.Gpt5Nano]		: 0.25,
				[EnumAiModel.Gpt5_1]		: 1,
				[EnumAiModel.Gpt5_6Luna]	: 0.8,
				[EnumAiModel.Gpt6Luna]		: 0.1,
				[EnumAiModel.Gpt6_1Terra]	: 0.3,
				[EnumAiModel.Gpt5_4Mini]	: 0.5
			}
		},
		costCalculateFunction: (options: IFlyingGenerateByKeywordsRequest & IUserFlyingUploadSettings) => {

			//0 - calculate the total active languages
			let languagesCount: number = 0;
			if (options.aiListing.mba.isActive) 		languagesCount += options.aiListing.mba.languages.length;
			if (options.aiListing.global.isActive) 		languagesCount += options.aiListing.global.languages.length;
			if (options.aiListing.displate.isActive) 	languagesCount += 1;

			//1 - calculate the costs
			const modelCost			: number = AI_LISTING_API_TOKEN_CONFIG[EnumApiTokenCostRoute.ListingByKeywords].apiTokenCosts.model[options.model];
			const numberOfEntries	: number = options.entries.length;

			//2 - calculate the total cost
			const totalCost: number = languagesCount * numberOfEntries * modelCost;
			return Util.Number.roundUp(totalCost);
		}
	},
	[EnumApiTokenCostRoute.ListingByTemplate]: {
		name			: 'Listing By Template API',
		description		: 'This is the description of Listing By Template API',
		reasonText		: 'Listing By Template',
		apiTokenCosts	: {
			model		: {
				[EnumAiModel.Gpt4o]			: 1,
				[EnumAiModel.Gpt4oMini]		: 1,
				[EnumAiModel.Gpt5]			: 0.85,
				[EnumAiModel.Gpt5Mini]		: 0.5,
				[EnumAiModel.Gpt5Nano]		: 0.25,
				[EnumAiModel.Gpt5_1]		: 1,
				[EnumAiModel.Gpt5_6Luna]	: 0.8,
				[EnumAiModel.Gpt6Luna]		: 0.1,
				[EnumAiModel.Gpt6_1Terra]	: 0.3,
				[EnumAiModel.Gpt5_4Mini]	: 0.5
			}
		},
		costCalculateFunction: (options: IFlyingGenerateByTemplateRequest & IUserFlyingUploadSettings) => {

			//0 - calculate the total active languages
			let languagesCount: number = 0;
			if (options.aiListing.mba.isActive) 		languagesCount += options.aiListing.mba.languages.length;
			if (options.aiListing.global.isActive) 		languagesCount += options.aiListing.global.languages.length;
			if (options.aiListing.displate.isActive) 	languagesCount += 1;

			//1 - calculate the costs
			const modelCost			: number = AI_LISTING_API_TOKEN_CONFIG[EnumApiTokenCostRoute.ListingByTemplate].apiTokenCosts.model[options.model];
			const numberOfEntries	: number = options.entries.length;

			//2 - calculate the total cost
			const totalCost: number = languagesCount * numberOfEntries * modelCost;
			return Util.Number.roundUp(totalCost);
		}
	}
};


/**------------------------------------------------------
 * Interfaces
 */
interface IAiListingApiTokenConfig {
	[EnumApiTokenCostRoute.ListingByVision]: IApiTokenCostConfigEntry<IFlyingGenerateByVisionRequest & IUserFlyingUploadSettings, {
		model	: Record<EnumAiModel, number>;
	}>;
	[EnumApiTokenCostRoute.FlaiAgent]: IApiTokenCostConfigEntry<IFlyingFlaiAgentRequest & IUserFlyingUploadSettings, {
		model	: Record<EnumAiModel, number>;
	}>;
	[EnumApiTokenCostRoute.MbaSetupByVision]: IApiTokenCostConfigEntry<IFlyingMbaSetupRequest, {
		model	: Record<EnumAiModel, number>;
	}>;
	[EnumApiTokenCostRoute.ListingByKeywords]: IApiTokenCostConfigEntry<IFlyingGenerateByKeywordsRequest & IUserFlyingUploadSettings, {
		model	: Record<EnumAiModel, number>;
	}>;
	[EnumApiTokenCostRoute.ListingByTemplate]: IApiTokenCostConfigEntry<IFlyingGenerateByTemplateRequest & IUserFlyingUploadSettings, {
		model	: Record<EnumAiModel, number>;
	}>;
}
